import {
  Accordion,
  AccordionTypes,
  Checkbox,
  ErrorBoundary,
  Heading,
  Icon,
  SelectCheckboxes,
  Shadow,
  ShadowStyles,
  Text,
  HStack,
  VStack,
} from '@genial/components';
import { Box, Pressable, useTheme } from 'native-base';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

import { FundComparatorResultNormalized } from '../../../../../../store/product/types';
import {
  fundsComparatorAvailablePeriods,
  fundsComparatorIndexesColors,
  fundsComparatorProductsColors,
} from '../../constants';

import { FundComparatorChartTooltip } from './components/FundComparatorChartTooltip';

namespace FundComparatorChartTypes {
  export type Props = {
    isLoading: boolean;
    data?: FundComparatorResultNormalized;
    indexes: any[];
    period: number;
    onSelectIndexes: (indexes: string[]) => void;
    onSelectPeriod: (period: number) => void;
    isMobile?: boolean;
    error?: Error;
    refresher: () => void;
  };
}

export const FundComparatorChart: React.FC<FundComparatorChartTypes.Props> = ({
  isLoading,
  data,
  indexes,
  period,
  onSelectIndexes,
  onSelectPeriod,
  isMobile,
  error,
  refresher,
}) => {
  const theme = useTheme();

  const [visibleProductsAccordionStatus, setVisibleProductsAccordionStatus] =
    useState<AccordionTypes.Status>('closed');

  const productsAsOptions = useMemo(() => {
    const uniqueProducts = new Map<string, FundComparatorResultNormalized['products'][number]>();

    data?.products.forEach((p) => {
      uniqueProducts.set(p.id, p);
    });

    return Array.from(uniqueProducts.values()).map((p, index) => ({
      value: p.id,
      label: p.productName,
      color: fundsComparatorProductsColors[index] as any,
    }));
  }, [data]);

  const fundComparatorIndexesAsOptions = useMemo(() => {
    const uniqueIndexes = new Map<string, FundComparatorResultNormalized['indexes'][number]>();

    data?.indexes.forEach((i) => {
      uniqueIndexes.set(i.id, i);
    });

    return (
      Array.from(uniqueIndexes.values()).map((i, index) => ({
        value: i.id,
        label: i.indexName,
        color: fundsComparatorIndexesColors[index] as any,
      })) || []
    );
  }, [data]);

  const selectPlaceholder = useMemo(() => {
    let placeholder = 'Selecione';

    if (indexes.length >= 1) {
      placeholder = fundComparatorIndexesAsOptions.find((i) => i.value === indexes[0])?.label || '';
    }

    if (indexes.length > 1) {
      placeholder += ` + ${indexes.length - 1}`;
    }

    return placeholder;
  }, [indexes]);

  const [visibleProducts, setVisibleProducts] = useState<string[]>([]);

  const handleVisibleProductsAccordionShow = useCallback(() => {
    setVisibleProductsAccordionStatus('opened');
  }, []);

  const handleVisibleProductsAccordionClosed = useCallback(() => {
    setVisibleProductsAccordionStatus('closed');
  }, []);

  const handleIndexesChangeOnCheckbox = useCallback(
    (value: string, checked: boolean) => {
      if (checked) {
        onSelectIndexes([...indexes, value]);
      } else {
        onSelectIndexes(indexes.filter((index) => index !== value));
      }
    },
    [onSelectIndexes, indexes]
  );

  const handleIndexesChangeOnSelect = useCallback(
    (selectedIndexes: string[]) => {
      onSelectIndexes(selectedIndexes);
    },
    [onSelectIndexes]
  );

  const handleSelectPeriod = useCallback(
    (selectedPeriod: number) => {
      onSelectPeriod(selectedPeriod);
    },
    [onSelectPeriod]
  );

  const handleVisibleProductsChange = useCallback(
    (value: string, checked: boolean) => {
      if (checked) {
        setVisibleProducts([...visibleProducts, value]);
      } else {
        setVisibleProducts(visibleProducts.filter((product) => product !== value));
      }
    },
    [visibleProducts]
  );

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!data) {
      return [];
    }

    // Group points by normalized date and flatten into recharts-friendly shape
    // Each row will look like: { date: Date, dateNormalized: string, 'p:<productId>': number, 'i:<indexId>': number }
    const grouped = new Map<string, any>();

    // Add product series values
    data.products.forEach((p) => {
      const key = p.dateNormalized;
      const row = grouped.get(key) || { date: p.date, dateNormalized: p.dateNormalized };
      row[`p:${p.id}`] = p.value;
      grouped.set(key, row);
    });

    // Add selected index series values
    const selectedIndexes = new Set(indexes);
    data.indexes.forEach((i) => {
      if (!selectedIndexes.has(i.id)) {
        return;
      }
      const key = i.dateNormalized;
      const row = grouped.get(key) || { date: i.date, dateNormalized: i.dateNormalized };
      row[`i:${i.id}`] = i.value;
      grouped.set(key, row);
    });

    // Sort by date asc
    const rows = Array.from(grouped.values()).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    return rows;
  }, [data, indexes]);

  // Generate chart lines for products and indexes
  const chartLines = useMemo(() => {
    if (!data) {
      return [];
    }

    const lines: any[] = [];
    // Unique product and index ids
    const productIds = Array.from(new Set(data.products.map((p) => p.id)));
    const indexIds = Array.from(new Set(data.indexes.map((i) => i.id))).filter((id) =>
      indexes.includes(id)
    );

    // Build product lines
    productIds.forEach((id, idx) => {
      lines.push({
        key: `p:${id}`,
        name: data.products.find((p) => p.id === id)?.productName,
        color: fundsComparatorProductsColors[idx % fundsComparatorProductsColors.length],
        dataKey: `p:${id}`,
        show: visibleProducts.includes(id),
      });
    });

    // Build index lines
    indexIds.forEach((id, index) => {
      lines.push({
        key: `i:${id}`,
        name: data.indexes.find((i) => i.id === id)?.indexName,
        color: fundsComparatorIndexesColors[index],
        dataKey: `i:${id}`,
        show: true,
      });
    });

    return lines;
  }, [data, indexes, visibleProducts]);

  const [maxYDomain, minYDomain] = useMemo(() => {
    if (!data) {
      return [0, 0];
    }

    const productValues = data.products.map((p) => p.value);
    const selectedIndexesSet = new Set(indexes);
    const indexValues = data.indexes
      .filter((i) => selectedIndexesSet.has(i.id))
      .map((i) => i.value);
    const allValues = [...productValues, ...indexValues];
    if (allValues.length === 0) {
      return [0, 0];
    }
    const max = Math.ceil(Math.max(...allValues));
    const min = Math.floor(Math.min(...allValues));
    return [max, min];
  }, [data, indexes]);

  useEffect(() => {
    setVisibleProducts(productsAsOptions.map((p) => p.value));
  }, [productsAsOptions]);

  useEffect(() => {
    setVisibleProductsAccordionStatus('closed');
  }, [isMobile]);

  if (error) {
    return <ErrorBoundary onRefresh={refresher} />;
  }

  return (
    <Shadow level={1} padding={{ base: 'inset.xs', sm: 'inset.sm' }} borderRadius="sm" flex={1}>
      <VStack space={{ base: 'stack.xxxs', sm: 'stack.xxs' }} flex={1}>
        <HStack alignItems="center" justifyContent="space-between">
          <Heading testId="rentability-heading" size={{ base: 'h3', sm: 'h2' }}>
            Rentabilidade
          </Heading>
          {isMobile && (
            <HStack space="stack.quarck" alignItems="center">
              <Text color="neutral.medium">Comparativos:</Text>
              <SelectCheckboxes
                options={fundComparatorIndexesAsOptions}
                onValueChange={handleIndexesChangeOnSelect}
                selectedValues={indexes}
                placeholder={selectPlaceholder}
              />
            </HStack>
          )}
        </HStack>
        <HStack space="stack.xxxs" mb={4}>
          {fundsComparatorAvailablePeriods.map((availablePeriod) => (
            <Pressable
              key={availablePeriod.value}
              onPress={() => handleSelectPeriod(availablePeriod.value)}
              paddingY="inset.nano"
              paddingX="inset.xs"
              borderRadius="pill"
              backgroundColor={
                period === availablePeriod.value ? 'brand.primary.principal' : 'neutral.white'
              }
              {...ShadowStyles.Level[1]}>
              <Text color={period === availablePeriod.value ? 'white' : 'brand.primary.principal'}>
                {availablePeriod.label}
              </Text>
            </Pressable>
          ))}
        </HStack>

        <Box flex={1} width="100%">
          {isLoading && (
            <Box flex={1} justifyContent="center" alignItems="center">
              <Text>Carregando...</Text>
            </Box>
          )}

          {!isLoading && !chartData.length ? (
            <VStack alignItems="center" space="stack.nano" flex={1} justifyContent="center">
              <Icon name="Info" size="xl" color="neutral.medium" />
              <VStack alignItems="center" space="stack.nano">
                <Text variant="semibold" fontSize="lg">
                  Não foi possível gerar o gráfico
                </Text>
                <Text>Não há dados suficientes para construir o gráfico.</Text>
              </VStack>
            </VStack>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis
                  dataKey="dateNormalized"
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  fontFamily={theme.fontConfig.SourceSansPro[400].normal}
                />
                <YAxis
                  domain={[minYDomain, maxYDomain]}
                  tickFormatter={(value) => `${value}%`}
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                  fontFamily={theme.fontConfig.SourceSansPro[400].normal}
                />
                <Tooltip content={<FundComparatorChartTooltip />} />
                {chartLines.map((line) => (
                  <Line
                    type="linear"
                    connectNulls={false}
                    key={line.key}
                    dataKey={line.dataKey}
                    name={line.name}
                    stroke={line.color}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                    display={line.show ? 'default' : 'none'}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>

        {isMobile ? (
          <Accordion
            status={visibleProductsAccordionStatus}
            onPress={handleVisibleProductsAccordionShow}
            onClosed={handleVisibleProductsAccordionClosed}
            hasShadow={false}>
            <Accordion.Visible>
              <Pressable onPress={handleVisibleProductsAccordionShow}>
                <Text variant="semibold" fontSize="md">
                  {visibleProducts.length} Ativos em análise
                </Text>
              </Pressable>
            </Accordion.Visible>
            <Accordion.Expanded>
              <Checkbox
                options={productsAsOptions}
                selectedValues={visibleProducts}
                onChange={handleVisibleProductsChange}
                testId="products-checkbox"
                columns={1}
                vertical
              />
            </Accordion.Expanded>
          </Accordion>
        ) : (
          <Box flexDirection="row" justifyContent="space-between" width="100%">
            <VStack space="stack.nano" mt={4}>
              <Text>Classe de ativo</Text>
              {productsAsOptions.length > 0 ? (
                <Checkbox
                  options={productsAsOptions}
                  selectedValues={visibleProducts}
                  onChange={handleVisibleProductsChange}
                  columns={3}
                  vertical
                  testId="products-checkbox"
                />
              ) : (
                <Text>Nenhum ativo encontrado</Text>
              )}
            </VStack>

            {fundComparatorIndexesAsOptions.length > 0 ? (
              <VStack space="stack.nano" mt={4}>
                <Text>Comparar com:</Text>
                <Checkbox
                  options={fundComparatorIndexesAsOptions}
                  selectedValues={indexes}
                  onChange={handleIndexesChangeOnCheckbox}
                  testId="indexes-checkbox"
                />
              </VStack>
            ) : null}
          </Box>
        )}
      </VStack>
    </Shadow>
  );
};
